import { ApiInterceptorConfiguration, ApiInterceptorServiceConfiguration } from '@blk/aw-bff-typescript-models';
import { search } from '@jmespath-community/jmespath';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_TIMEOUT, EXTENDED_API_TIMEOUT } from '../../APIConstants';
import {
  applyAGraphResponseTransformations,
  getAGraphBaseUrl,
  getApiBaseUrlSuffix,
  modifyAGraphURL,
  ResponseTransformConfig,
  setAGraphHeaders,
} from '../../utils';
import { AGraphApiSettings } from '../Base';
import { isAGraphEnabled } from './AGraphUtils';
import { ALADDIN_WEALTH_SERVER_API_URL, ALADDIN_WEALTH_SERVER_API_URL_AGRAPH } from './Constants';

export const awSettings: AGraphApiSettings & {
  getApiInterceptorConfigAndContextFn?: () => {
    apiInterceptorConfiguration: ApiInterceptorConfiguration | undefined;
    isHighEntitlementAlternativeExperience: boolean;
  };
  getPkgFn?: () => string | undefined;
  getViewAsLoginFn?: () => string | undefined;
} = {
  getApiInterceptorConfigAndContextFn: undefined,
  getPkgFn: undefined,
  getViewAsLoginFn: undefined,
};

const updateConfig = (api: AxiosInstance, withPkg: boolean, timeout?: number) => {
  api.interceptors.request.use((config) => {
    const {
      getAladdinApiKey,
      getConfigurationDirectoryFn,
      getEnvTypeFn,
      getPkgFn,
      getViewAsLoginFn,
      getApiInterceptorConfigAndContextFn,
    } = awSettings;
    const configurationDirectory = getConfigurationDirectoryFn?.();
    const envType = getEnvTypeFn?.();
    const enableAGraph = isAGraphEnabled();
    let baseURL = ALADDIN_WEALTH_SERVER_API_URL + getApiBaseUrlSuffix(envType, '_', true);
    if (enableAGraph) {
      // The baseURL ends with /v1 - but in a couple of cases we want to use /v2 instead.
      let aGraphBaseUrl = ALADDIN_WEALTH_SERVER_API_URL_AGRAPH;
      if (config.url?.startsWith('/v2')) {
        aGraphBaseUrl = ALADDIN_WEALTH_SERVER_API_URL_AGRAPH.split('/v1')[0] + '';
      }
      baseURL = getAGraphBaseUrl(aGraphBaseUrl);
    }
    const viewAs = getViewAsLoginFn?.();
    const pkg = config.params?.pkg ?? getPkgFn?.();
    const apiInterceptorConfigurationAndContext = getApiInterceptorConfigAndContextFn?.();
    const interceptConfig: ApiInterceptorServiceConfiguration | undefined =
      apiInterceptorConfigurationAndContext?.apiInterceptorConfiguration?.aladdinWealthServer.find((ic) =>
        config.url?.includes(ic.url)
      );
    let finalData = config.data;
    if (interceptConfig !== undefined) {
      if (
        interceptConfig.conditionType === 'largeScaleHighEntitlement' &&
        apiInterceptorConfigurationAndContext?.isHighEntitlementAlternativeExperience
      ) {
        const { expressions } = interceptConfig.payloadTransformer;
        expressions.forEach((expression) => {
          try {
            const result = search(finalData, expression, { enable_legacy_literals: true });
            finalData = result;
          } catch (error) {
            console.warn(`API interceptor warning [${config.url}]: ${error}`);
          }
        });
      }
    }

    if (withPkg && !pkg) {
      throw Error('You must define a function for awSettings.getPkgFn before using this awApi');
    }
    const { disableViewAs, ...configParams } = config.params || {};
    const updatedConfig: InternalAxiosRequestConfig = {
      ...config,
      baseURL,
      timeout: timeout || API_TIMEOUT,
      params: {
        ...configParams,
        viewAs: disableViewAs ? undefined : viewAs,
        ...(withPkg ? { pkg } : {}),
        configurationDirectory,
      },
      data: finalData,
    };
    setAGraphHeaders(updatedConfig, getAladdinApiKey?.(), envType);
    if (enableAGraph) {
      modifyAGraphURL(updatedConfig, ['api/v1', 'api/v2', 'api/v3']);
    }
    return updatedConfig;
  });
};

export const awApi = axios.create();

export const awApiWithPkg = axios.create();

export const awApiWithPkgAndExtendedTimeout = axios.create();

updateConfig(awApi, false);

updateConfig(awApiWithPkg, true);

updateConfig(awApiWithPkgAndExtendedTimeout, true, EXTENDED_API_TIMEOUT);

// Apply response transformations for AGraph API
[awApi, awApiWithPkg, awApiWithPkgAndExtendedTimeout].forEach((api) => {
  api.interceptors.response.use((response) => {
    if (isAGraphEnabled() && response.data) {
      const AGRAPH_RESPONSE_TRANSFORMS: ResponseTransformConfig[] = [
        {
          urlPattern: '/events',
          method: 'POST',
          timestampFields: ['creationTimestamp'],
        },
        {
          urlPattern: '/fa-accounts/full-data',
          method: 'POST',
          timestampFields: [
            'creationTimestamp',
            'expiryTimestamp',
            'lastUpdatedAnalysisTimestamp',
            'lastUpdatedProposalTimestamp',
            'lastUpdatedTimestamp',
          ],
          flattenFieldValueMaps: { arrayFieldPath: 'faAccountsDataResponse.accounts' },
        },
        {
          urlPattern: '/workflow/item',
          method: 'POST',
          timestampFields: ['timestamp'],
        },
      ];
      applyAGraphResponseTransformations(response, AGRAPH_RESPONSE_TRANSFORMS);
    }

    return response;
  });
});
